import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/pro-light-svg-icons/faLock';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { SMLUpgradeFeature } from '../config/music24PlanCapabilities';
import { planNameMessages } from '../config/music24PlanNames';
import { formatPlanPrice, useDisplayCurrency } from '../config/useDisplayCurrency';
import { useNextPlanForFeature } from '../hooks/useNextPlanForFeature';
import { useUpgradePrompt } from '../hooks/useUpgradePrompt';
import { useIsNativeEmbed } from '../navigation/hooks/useIsNativeEmbed';
import { fansMessages as messages } from './fansMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet per screen; each part of the screen uses its own slice of it */
import styles from './Fans.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface FansPlanLockCardProps {
  /** The subscribers of the existing list: the title names them. */
  total: number;
}

/**
 * The plan card above the Fans content on Free (design c6). It has the hooks of
 * `StreamingPlanLockCard` (the next plan, its price, no price in a native web view), in the row
 * layout of the frame: the result first, then the plan chip and "See plans". The numbers under it
 * stay readable: they are the reason to upgrade.
 */
const FansPlanLockCardComponent: React.FC<FansPlanLockCardProps> = ({ total }) => {
  const intl = useIntl();
  const { nextPlan } = useNextPlanForFeature(SMLUpgradeFeature.presave);
  const currency = useDisplayCurrency();
  const isNativeEmbed = useIsNativeEmbed();
  const openUpgradePrompt = useUpgradePrompt();
  const upgrade = useCallback(() => openUpgradePrompt(SMLUpgradeFeature.presave), [openUpgradePrompt]);

  const plan = nextPlan === null ? null : intl.formatMessage(planNameMessages[nextPlan.tier]);
  const planChip =
    nextPlan === null || plan === null
      ? null
      : isNativeEmbed
        ? intl.formatMessage(messages.lockPlanNoPrice, { plan })
        : intl.formatMessage(messages.lockPlan, {
            plan,
            price: formatPlanPrice(intl, nextPlan.prices.monthly.amount, currency),
          });

  return (
    <div className={styles.planCard} data-testid="fans-plan-lock">
      <span className={styles.planIcon}>
        <FontAwesomeIcon icon={faLock} />
      </span>
      <div className={styles.planText}>
        <b>{intl.formatMessage(messages.lockTitle, { count: total })}</b>
        <p>{intl.formatMessage(messages.lockText)}</p>
      </div>
      <div className={styles.planActions}>
        {planChip === null ? null : <span className={styles.lockChip}>{planChip}</span>}
        <SMLButton size="sm" color="primary" omitCap onClick={upgrade}>
          {intl.formatMessage(messages.lockButton)}
        </SMLButton>
      </div>
    </div>
  );
};

export const FansPlanLockCard = TypedMemo(FansPlanLockCardComponent);
